import { supabase } from '../../lib/supabase';
import { ELEVATED_CASE_PRIORITIES } from '../../lib/casePriority';
import { NON_INTERNAL_MESSAGES_OR_FILTER } from '../../lib/caseUnreadMessages';
import {
  ORDER_SUBMISSION_PHASE_NAME_ILIKE_COMMANDE,
  ORDER_SUBMISSION_PHASE_NAME_ILIKE_SOUMISSION,
  caseRowMatchesNonSoumiseConception,
  milestoneNeedsDentistAction,
} from '../../lib/dentistCaseQueue';

/** Début du mois civil courant et du mois précédent (ISO). */
function calendarMonthBounds(reference = new Date()) {
  const y = reference.getFullYear();
  const m = reference.getMonth();
  const thisStart = new Date(y, m, 1, 0, 0, 0, 0);
  const prevStart = new Date(y, m - 1, 1, 0, 0, 0, 0);
  return {
    thisStartIso: thisStart.toISOString(),
    prevStartIso: prevStart.toISOString(),
  };
}

/** Tendance mois sur mois pour les KPI (affichage type capture). */
export function formatMoMTrend(current, previous) {
  if (previous === 0) {
    if (current === 0) return null;
    return '+100%';
  }
  const pct = ((current - previous) / previous) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

/**
 * Comptages bandeau KPI + patients / conceptions terminées par mois civil.
 */
export async function fetchDentistHubStats(userId) {
  const empty = {
    activeCount: 0,
    urgentCount: 0,
    unreadCount: 0,
    draftCount: 0,
    newPatientsThisMonth: 0,
    newPatientsPrevMonth: 0,
    completedThisMonth: 0,
    completedPrevMonth: 0,
  };
  if (!userId) return empty;

  const { thisStartIso, prevStartIso } = calendarMonthBounds();

  const [
    activeRes,
    urgentRes,
    draftRes,
    unreadRes,
    patientsThisRes,
    patientsPrevRes,
    completedThisRes,
    completedPrevRes,
  ] = await Promise.all([
    supabase
      .from('cases')
      .select('id', { count: 'exact', head: true })
      .eq('user_profile_id', userId)
      .not('status', 'in', '("completed","cancelled","draft")'),
    supabase
      .from('cases')
      .select('id', { count: 'exact', head: true })
      .eq('user_profile_id', userId)
      .in('priority', ELEVATED_CASE_PRIORITIES)
      .not('status', 'in', '("completed","cancelled")'),
    supabase
      .from('cases')
      .select('id', { count: 'exact', head: true })
      .eq('user_profile_id', userId)
      .eq('status', 'draft'),
    supabase
      .from('case_messages')
      .select('id, cases!inner(user_profile_id)', { count: 'exact', head: true })
      .eq('is_read', false)
      .or(NON_INTERNAL_MESSAGES_OR_FILTER)
      .neq('sender_id', userId)
      .eq('cases.user_profile_id', userId),
    supabase
      .from('patients')
      .select('id', { count: 'exact', head: true })
      .eq('user_profile_id', userId)
      .gte('registration_date', thisStartIso),
    supabase
      .from('patients')
      .select('id', { count: 'exact', head: true })
      .eq('user_profile_id', userId)
      .gte('registration_date', prevStartIso)
      .lt('registration_date', thisStartIso),
    supabase
      .from('cases')
      .select('id', { count: 'exact', head: true })
      .eq('user_profile_id', userId)
      .eq('status', 'completed')
      .gte('updated_at', thisStartIso),
    supabase
      .from('cases')
      .select('id', { count: 'exact', head: true })
      .eq('user_profile_id', userId)
      .eq('status', 'completed')
      .gte('updated_at', prevStartIso)
      .lt('updated_at', thisStartIso),
  ]);

  return {
    activeCount: activeRes.count ?? 0,
    urgentCount: urgentRes.count ?? 0,
    draftCount: draftRes.count ?? 0,
    unreadCount: unreadRes.count ?? 0,
    newPatientsThisMonth: patientsThisRes.count ?? 0,
    newPatientsPrevMonth: patientsPrevRes.count ?? 0,
    completedThisMonth: completedThisRes.count ?? 0,
    completedPrevMonth: completedPrevRes.count ?? 0,
  };
}

/**
 * Derniers messages non lus (lien rapide messagerie).
 */
export async function fetchRecentUnreadMessages(userId, limit = 3) {
  const { data, error } = await supabase
    .from('case_messages')
    .select(
      `
      id,
      case_id,
      created_at,
      message_text,
      cases!inner(
        id,
        case_number,
        user_profile_id,
        patient:patients(name)
      )
    `
    )
    .eq('is_read', false)
    .or(NON_INTERNAL_MESSAGES_OR_FILTER)
    .neq('sender_id', userId)
    .eq('cases.user_profile_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[dentistHubData] fetchRecentUnreadMessages', error);
    return [];
  }
  return (data || []).map((r) => ({
    id: r.id,
    caseId: r.case_id,
    caseNumber: r.cases?.case_number,
    patientName: r.cases?.patient?.name || '—',
    createdAt: r.created_at,
    preview: (r.message_text || '').slice(0, 80),
  }));
}

export async function fetchDraftCases(userId, limit = 7) {
  const { data, error } = await supabase
    .from('cases')
    .select('id, case_number, updated_at, patient:patients(name)')
    .eq('user_profile_id', userId)
    .eq('status', 'draft')
    .order('updated_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data || []).map((c) => ({
    id: c.id,
    caseNumber: c.case_number,
    patientName: c.patient?.name || '—',
    updatedAt: c.updated_at || null,
  }));
}

export async function fetchRecentIncidents(userId, limit = 5) {
  const { data, error } = await supabase
    .from('case_incidents')
    .select('id, title, created_at, case_id, cases!inner(id, case_number, user_profile_id, patient:patients(name))')
    .eq('cases.user_profile_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[dentistHubData] fetchRecentIncidents', error);
    return [];
  }

  return (data || []).map((row) => ({
    id: row.id,
    title: row.title || 'Incident',
    caseId: row.case_id,
    caseNumber: row.cases?.case_number || '—',
    patientName: row.cases?.patient?.name || '—',
    reportedDate: row.created_at
      ? new Date(row.created_at).toLocaleDateString('fr-FR')
      : '—',
  }));
}

/** Jalon timeline : révision attendue du dentiste (aligné filtre liste des cas). */
export const DENTIST_REVISION_PHASE_ILIKE = '%Révision du dentiste%';

/**
 * Compteurs pour les 3 boutons « Actions requises » (même logique que le filtre liste des cas).
 */
export async function fetchDentistQueueCounts(userId) {
  const empty = { nonSoumis: 0, aValider: 0 };
  if (!userId) return empty;

  const [draftRes, submitPhaseRes, revisionRes, revisionMilestoneRes] = await Promise.all([
    supabase.from('cases').select('id').eq('user_profile_id', userId).eq('status', 'draft'),
    supabase
      .from('case_timeline_phases')
      .select('case_id, cases!inner(status)')
      .eq('cases.user_profile_id', userId)
      .eq('group', 1)
      .eq('phase_order', 1)
      .in('status', ['pending', 'in_progress'])
      .ilike('phase_name', ORDER_SUBMISSION_PHASE_NAME_ILIKE_SOUMISSION)
      .ilike('phase_name', ORDER_SUBMISSION_PHASE_NAME_ILIKE_COMMANDE),
    supabase
      .from('case_timeline_phases')
      .select('case_id, cases!inner(user_profile_id, status)')
      .eq('cases.user_profile_id', userId)
      .ilike('phase_name', DENTIST_REVISION_PHASE_ILIKE)
      .eq('status', 'in_progress'),
    supabase
      .from('phase_milestones')
      .select(`
        milestone_name,
        is_completed,
        phase:case_timeline_phases!inner(
          case_id,
          status,
          cases!inner(user_profile_id, status)
        )
      `)
      .eq('phase.cases.user_profile_id', userId)
      .eq('is_completed', false),
  ]);

  if (draftRes.error) {
    console.warn('[dentistHubData] fetchDentistQueueCounts drafts', draftRes.error);
  }
  if (revisionRes.error) {
    console.warn('[dentistHubData] fetchDentistQueueCounts revision', revisionRes.error);
  }
  if (revisionMilestoneRes.error) {
    console.warn('[dentistHubData] fetchDentistQueueCounts revision milestones', revisionMilestoneRes.error);
  }
  if (submitPhaseRes.error) {
    console.warn('[dentistHubData] fetchDentistQueueCounts submitPhase', submitPhaseRes.error);
  }
  const draftIds = (draftRes.data || []).map((r) => r.id);
  const submitPhaseIds = (submitPhaseRes.data || []).map((row) => row.case_id);
  const candidateIds = [...new Set([...draftIds, ...submitPhaseIds])];

  let nonSoumisCount = 0;
  if (candidateIds.length > 0) {
    const [{ data: caseRows }, { data: g1PhaseRows }] = await Promise.all([
      supabase.from('cases').select('id, status').eq('user_profile_id', userId).in('id', candidateIds),
      supabase
        .from('case_timeline_phases')
        .select('case_id, phase_name, status, group, phase_order')
        .in('case_id', candidateIds)
        .eq('group', 1),
    ]);

    const statusById = Object.fromEntries((caseRows || []).map((r) => [r.id, r.status]));
    const phasesByCase = {};
    (g1PhaseRows || []).forEach((row) => {
      if (!phasesByCase[row.case_id]) phasesByCase[row.case_id] = [];
      phasesByCase[row.case_id].push(row);
    });

    nonSoumisCount = candidateIds.filter((id) =>
      caseRowMatchesNonSoumiseConception({
        status: statusById[id],
        timelinePhases: phasesByCase[id] || [],
      })
    ).length;
  }

  const revisionIds = new Set();
  (revisionRes.data || []).forEach((row) => {
    if (!['completed', 'cancelled'].includes(row.cases?.status)) {
      revisionIds.add(row.case_id);
    }
  });
  (revisionMilestoneRes.data || []).forEach((row) => {
    const phase = row.phase;
    const caseId = phase?.case_id;
    const caseStatus = phase?.cases?.status;
    if (!caseId || ['completed', 'cancelled'].includes(caseStatus)) return;
    if (!['pending', 'in_progress'].includes(phase?.status)) return;
    if (!milestoneNeedsDentistAction(row)) return;
    revisionIds.add(caseId);
  });

  return {
    nonSoumis: nonSoumisCount,
    aValider: revisionIds.size,
  };
}

export async function loadDentistHubData(userId) {
  const [stats, draftCases, queueCounts, messageList, recentIncidents] = await Promise.all([
    fetchDentistHubStats(userId),
    fetchDraftCases(userId, 7),
    fetchDentistQueueCounts(userId),
    fetchRecentUnreadMessages(userId, 3),
    fetchRecentIncidents(userId, 5),
  ]);

  return {
    stats,
    queueCounts,
    messageSamples: messageList,
    recentIncidents,
    draftCases,
  };
}
